import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { loadFestivalBySlug } from '../../utils/cmsContentLoader';
import { Seo, Loading, EmptyContent, ContentAdSlot } from '../shared';
import ExploreHero from '../explore/ExploreHero';
import QuestionBlock from '../explore/QuestionBlock';
import KnowledgeFlow from '../explore/KnowledgeFlow';
import { EXPLORE_IMAGES } from '../../data/exploreImages';
import '../../styles/guides.scss';
import '../../styles/explore.scss';
import '../../styles/media.scss';

const DATE_TYPE_NOTE = {
	lunar_calculated: 'This is a lunar-calendar festival — the exact date shifts every year. Confirm the specific date for your travel year rather than assuming last year\'s date repeats.',
	variable: 'This date varies by year and sometimes by region — confirm closer to your trip.',
	fixed: 'This festival falls on a fixed calendar date each year.',
	solar_fixed: 'This festival falls on a fixed solar calendar date each year (minor drift possible in leap years).',
};

function FestivalRichText({ text }) {
	if (!text) return null;
	const paragraphs = text.split(/\n\n+/).filter(Boolean);
	return (
		<div className="guide-detail__body">
			{paragraphs.map((paragraph, i) => {
				const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
				return (
					<p key={i}>
						{parts.map((part, j) => {
							if (part.startsWith('**') && part.endsWith('**')) {
								return <strong key={j}>{part.slice(2, -2)}</strong>;
							}
							return part;
						})}
					</p>
				);
			})}
		</div>
	);
}

function FestivalDetailPage() {
	const { slug } = useParams();
	const [festival, setFestival] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);

	useEffect(() => {
		setLoading(true);
		setNotFound(false);
		loadFestivalBySlug(slug)
			.then((f) => {
				if (f) setFestival(f);
				else setNotFound(true);
			})
			.catch(() => setNotFound(true))
			.finally(() => setLoading(false));
	}, [slug]);

	if (loading) return <Loading />;

	if (notFound || !festival) {
		return (
			<div className="container py-5">
				<EmptyContent>
					<i className="fas fa-calendar text-muted" style={{ fontSize: '2rem' }} aria-hidden="true" />
					<h3 className="mt-3">Festival not found</h3>
					<Link to="/festivals" className="btn btn-primary mt-2">Browse the Festivals Hub</Link>
				</EmptyContent>
			</div>
		);
	}

	const f = festival;
	const districts = f.districts || [];
	const destinations = f.destinations || [];
	const whereParts = [f.region && f.region.name, ...districts.map((d) => d.name)].filter(Boolean);
	const dateNote = DATE_TYPE_NOTE[f.date_type];

	return (
		<div className="explore-page guide-detail-page">
			<Seo title={`${f.name} — Nepal Festival Guide`} path={`/festivals/${f.slug}`} description={f.summary || f.tagline} />

			<ExploreHero
				image={EXPLORE_IMAGES.festival}
				eyebrow="Festivals Hub"
				title={f.name}
				lead={f.tagline || f.summary}
				compact
			/>

			<div className="container guide-detail explore-body" style={{ paddingTop: '2rem' }}>
				<nav className="guide-breadcrumb mb-3" aria-label="Breadcrumb">
					<Link to="/destinations">Explore Nepal</Link>
					{' / '}
					<Link to="/festivals">Festivals</Link>
					{' / '}
					<span>{f.name}</span>
				</nav>

				{f.summary && (
					<p className="guide-detail__summary" style={{ fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '2rem' }}>
						{f.summary}
					</p>
				)}

				<QuestionBlock id="when" question="When does it happen?">
					<p><strong>Typically:</strong> {f.typical_month || 'Varies by year'}</p>
					{dateNote && <p>{dateNote}</p>}
					<p>Check the <Link to="/seasonal-discovery">seasonal guide</Link> and confirm the exact Gregorian date for your travel year before booking flights.</p>
				</QuestionBlock>

				<QuestionBlock id="where" question="Where does it happen?">
					{whereParts.length > 0 ? (
						<>
							<p>{whereParts.join(', ')}</p>
							{districts.length > 0 && (
								<ul>
									{districts.map((d) => (
										<li key={d.slug}><Link to={`/districts/${d.slug}`}>{d.name} District</Link></li>
									))}
								</ul>
							)}
						</>
					) : (
						<p>Celebrated nationwide, with the most visible public events in the Kathmandu Valley and major cities.</p>
					)}
				</QuestionBlock>

				<QuestionBlock id="story" question="What is the story and cultural significance?">
					<FestivalRichText text={f.body} />
				</QuestionBlock>

				<QuestionBlock id="experience" question="How can I experience it as a visitor?">
					<FestivalRichText text={f.how_to_participate} />
					<p>When in doubt, observe respectfully from a distance. Ask locally whether an event is open to visitors before participating.</p>
				</QuestionBlock>

				<QuestionBlock id="photos" question="Any photography tips?">
					<FestivalRichText text={f.photography_tips} />
					<p>Ask before photographing people at religious events. Never photograph cremations or private family rituals up close.</p>
				</QuestionBlock>

				{destinations.length > 0 && (
					<section className="destination-section">
						<h2>Related destinations</h2>
						<div className="guide-grid">
							{destinations.map((d) => (
								<Link key={d.slug} to={`/destinations/${d.slug}`} className="guide-card">
									<h3 className="guide-card__title">{d.name}</h3>
									{d.summary && <p className="guide-card__summary">{d.summary}</p>}
								</Link>
							))}
						</div>
					</section>
				)}

				<ContentAdSlot layout="festival" className="adsense-unit--in-content my-4" />

				<KnowledgeFlow
					steps={[
						{ label: 'Plan your travel dates', to: '/seasonal-discovery', desc: 'Month-by-month weather and festival calendar' },
						{ label: 'Explore cultural sites', to: '/destinations?tag=unesco-heritage', desc: 'Temples and heritage in the festival regions' },
						{ label: 'Sort out visa & entry', to: '/government', desc: 'Immigration requirements' },
						{ label: 'Book a cultural tour', to: '/packages', desc: 'Private tours timed to festival dates' },
					]}
				/>
			</div>
		</div>
	);
}

export default FestivalDetailPage;
